import * as React from "react";
import styled from "styled-components";
import { Route, Switch, useLocation, useParams } from "react-router";
import { PublishedRelease } from "../../../../../common/types/release/published-release";
import { getPublished, listMessages } from "../../sdk/release";
import { validateTracks } from "../../sdk/track";
import * as releaseAdmin from "../../sdk/release-admin";
// -------
import { ReleaseDetailContent } from "../../controllers/releases/detail/detail-content";
import { ReleaseDetailNavigation } from "../../controllers/releases/detail/detail-navigation";
import { ReleaseDetailMessages } from "../../controllers/releases/detail/detail-messages";
import { ReleaseDetailActions } from "../../controllers/releases/detail/detail-actions";
import { ReleaseDetailTrack } from "../../controllers/releases/detail/detail-track";
import { ReleaseSaveActions } from "../../controllers/releases/detail/detail-save";
import { ReleaseDetailChangelog } from "../../controllers/releases/detail/detail-changelog";
// -------
import { routes } from "../../routes";
import { Redirect } from "react-router-dom";
import LoadingCtrl from "../../controllers/loading";

import { TrackValidationResult } from "../../../../../services/track/types/track-validation-result";
import SimpleTrack from "../../../../../common/types/release/simple-track";
import { Collaborator } from "../../../../../services/collaborator/types/collaborator";
import { Release } from "../../../../../common/types/release/release";
import { IngestionMessage } from "../../../../../services/publisher-notifications/types/ingestion-message";
import { ReleaseDetailReprocess } from "./views/release-detail-reprocess";
import { ReleaseDetailDeleted } from "./views/release-detail-deleted";
import { ReleaseChangelog } from "../../../../../services/release-admin/types/release-changelog";
import { ReleaseNote } from "../../../../../services/release-admin/types/release-note";
import { BackofficeUserTier } from "../../../../../services/release-admin/types/backoffice-user-tier";
import { ReleaseDetailNotes } from "../../controllers/releases/detail/detail-notes";

interface ReleaseDetailPageProps {
  tier?: BackofficeUserTier;
}

const delayPromise = (seconds: number) => {
  return new Promise((resolve) => setTimeout(resolve, seconds * 1000));
};

export const ReleaseDetailPage: React.FC<ReleaseDetailPageProps> = ({
  tier,
}) => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();

  const [loading, setLoading] = React.useState(false);

  const [error, setError] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<{code?: string; description?: string}>({});
  const [release, setRelease] = React.useState<PublishedRelease>();
  const [original, setOriginal] = React.useState<PublishedRelease>();
  const [validation, setValidation] = React.useState<TrackValidationResult[]>();
  const [hasChanges, setChanged] = React.useState(false);
  const [messages, setMessages] = React.useState<IngestionMessage[]>([]);
  const [changelog, setChangelog] = React.useState<ReleaseChangelog[]>([]);
  const [notes, setNotes] = React.useState<ReleaseNote[]>([]);
  const [loadingNotes, setLoadingNotes] = React.useState(false);
  // const [showActions, setShowActions] = React.useState(false);
  const [showActions] = React.useState(true);

  const isInvalid =
    validation && validation.some((v) => !v.validation?.success);

  const loadRelease = React.useCallback(
    (releaseId: string) => {
      setError(false);
      setLoading(true);
      setRelease(undefined);
      setOriginal(undefined);

      releaseAdmin
        .listChangelog(releaseId)
        .then((response) => setChangelog(response.results));
      releaseAdmin
        .listNotes(releaseId)
        .then((response) => setNotes(response.results));

      // releaseAdmin.validateAssignment(releaseId)
      //   .then((isAssigned) => setShowActions(isAssigned || tier === 'tier-3'))

      getPublished(releaseId)
        .then((result) => {
          if (result.code){
            setErrorMessage(result);
            return;
          }
          setRelease(result as PublishedRelease);
          setOriginal(result as PublishedRelease);
          if (result.release.UPCEAN) {
            listMessages(result.release.UPCEAN).then((data) =>
              setMessages(data)
            );
          }
        })
        // tslint:disable-next-line
        .catch(async () => {
          setError(true);
          await validateTracks(id).then(setValidation);
        })
        .then(() => setLoading(false));
    },
    [id]
  );

  React.useEffect(() => {
    loadRelease(id);
  }, [id, loadRelease]);

  const handleAction = () => {
    // props.onChange(id);
  };

  const handleSave = (save: boolean) => {
    if (save) {
      setOriginal(release);
    } else {
      setRelease(original);
    }
    setChanged(false);
  };

  const handleNewNote = async (entry: string) => {
    if (!release?.id) {
      return;
    }
    setLoadingNotes(true);
    const note: ReleaseNote = await releaseAdmin.createNote(release.id, entry);
    setNotes([note].concat(notes));
    setLoadingNotes(false);
  };

  const handleTrackChange = (
    track: SimpleTrack,
    collaborators: Collaborator[]
  ) => {
    if (!release) {
      return;
    }

    const newRelease: PublishedRelease = {
      ...release,
      tracks: release.tracks.map((oldTrack) => {
        if (oldTrack.id === track.id) {
          return { ...track };
        }
        return oldTrack;
      }),
      collaborators: release.collaborators.map((oldCollaborator) => {
        const found = collaborators.find(
          (collaborator) => collaborator.id === oldCollaborator.id
        );
        if (found) {
          return { ...found };
        }
        return oldCollaborator;
      }),
    };

    setRelease(newRelease);
    setChanged(true);
  };

  const handleReleaseUpdate = <T extends keyof Release>(
    key: T,
    value: Release[T]
  ) => {
    if (!release) {
      return;
    }

    const newRelease: PublishedRelease = {
      ...release,
      release: {
        ...release.release,
        [key]: value,
      },
    };

    setRelease(newRelease);
    setChanged(true);
  };

  const handleReleaseReprocessed = async () => {
    await delayPromise(10);
    loadRelease(id);
  };

  // const simpleRelease = props.releases?.find((r) => r.id === id);

  if (errorMessage.code){
    return (
      <MainContainer narrow>
        <ReleaseDetailDeleted deleteMessage={errorMessage.description || '' }/>
      </MainContainer>
    )
  }
  if (error && !isInvalid) {
    return (
      <MainContainer narrow>
        <ReleaseDetailReprocess
          releaseId={id}
          onReleaseReprocessed={handleReleaseReprocessed}
        />
      </MainContainer>
    );
  }

  if (loading) {
    return (
      <MainContainer narrow={loading}>
        <LoaderContainer>
          <LoadingCtrl size="128px" loading />
        </LoaderContainer>
      </MainContainer>
    );
  }

  if (release) {
    return (
      <MainContainer narrow={loading}>
        <NavContainer>
          <ReleaseDetailNavigation
            published={release}
            messages={messages}
            changelog={changelog}
            notes={notes}
          />
        </NavContainer>
        <DetailContainer>
          <Switch>
            <Route
              exact
              path={`${routes.releases.detail}/${routes.releases.generalData}`}
            >
              <ReleaseDetailContent
                published={release}
                onUpdateField={handleReleaseUpdate}
              />
            </Route>
            <Route
              exact
              path={`${routes.releases.detail}/${routes.releases.trackData}`}
            >
              <ReleaseDetailTrack
                published={release}
                onChange={handleTrackChange}
              />
            </Route>
            <Route
              exact
              path={`${routes.releases.detail}/${routes.releases.messages}`}
            >
              <ReleaseDetailMessages messages={messages} />
            </Route>
            <Route
              exact
              path={`${routes.releases.detail}/${routes.releases.changelog}`}
            >
              <ReleaseDetailChangelog changelog={changelog} />
            </Route>
            <Route
              exact
              path={`${routes.releases.detail}/${routes.releases.notes}`}
            >
              <ReleaseDetailNotes
                notes={notes}
                setNotes={setNotes}
                onNewNote={handleNewNote}
                loading={loadingNotes}
              />
            </Route>
            <Route exact path={`${routes.releases.detail}`}>
              <Redirect
                to={{
                  pathname: `${routes.releases.list}/${id}/${routes.releases.generalData}`,
                  state: location.state,
                }}
              />
            </Route>
          </Switch>
        </DetailContainer>
        {showActions ? (
          <ActionsContainer>
            {hasChanges ? (
              <ReleaseSaveActions
                release={release.release}
                collaborators={release.collaborators}
                tracks={release.tracks}
                onSave={handleSave}
              />
            ) : (
              <ReleaseDetailActions
                status={release.release.status}
                data={[
                  {
                    release: release.release,
                    assets: release.assets,
                    tracks: release.tracks,
                  },
                ]}
                onAction={handleAction}
                tier={tier}
              />
            )}
          </ActionsContainer>
        ) : null}
      </MainContainer>
    );
  }

  return null;
};

const MainContainer = styled.section<{ narrow?: boolean }>`
  position: relative;
  margin: 3px auto;
  width: ${({ narrow }) => (narrow ? "15vw" : "90vw")};
  height: calc(100vh - 70px);
  overflow: hidden;
`;

const NavContainer = styled.nav`
  position: absolute;
  width: 20%;
  height: 100vh;
  top: 0;
  left: 0;
  bottom: auto;
  right: auto;
  overflow: auto;
  background: #333333;
`;

const DetailContainer = styled.section`
  position: absolute;
  width: 80%;
  top: 0;
  left: 20%;
  bottom: 50px;
  right: auto;
  overflow: auto;
  padding: 0.5rem;
`;

const ActionsContainer = styled.section`
  position: absolute;
  width: 80%;
  // height: 50px;
  top: auto;
  left: 20%;
  bottom: 0;
  right: auto;
  padding: 20px 8px;
  box-sizing: border-box;
`;
export const LoaderContainer = styled.div`
  background: rgba(255, 255, 255, 0.5);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;

  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  height: 128px;
`;
